import type { UseFormReturn } from "react-hook-form";
import type { DemandFormData } from "./DemandIntakeForm";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertCircle } from "lucide-react";

interface BasicInfoStepProps {
  form: UseFormReturn<DemandFormData>;
}

export function BasicInfoStep({ form }: BasicInfoStepProps) {
  const { register, formState: { errors }, setValue, watch } = form;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Demand Title */}
        <div className="space-y-2">
          <Label htmlFor="demandTitle">
            Demand Title <span className="text-destructive">*</span>
          </Label>
          <Input
            id="demandTitle"
            placeholder="Enter a clear, descriptive title"
            {...register("demandTitle", {
              required: "Demand title is required",
              minLength: {
                value: 5,
                message: "Title must be at least 5 characters"
              }
            })}
            className={errors.demandTitle ? "border-destructive" : ""}
          />
          {errors.demandTitle && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {errors.demandTitle.message}
            </div>
          )}
        </div>

        {/* Priority */}
        <div className="space-y-2">
          <Label htmlFor="priority">
            Priority Level <span className="text-destructive">*</span>
          </Label>
          <Select
            onValueChange={(value) => setValue("priority", value)}
            value={watch("priority")}
          >
            <SelectTrigger className={errors.priority ? "border-destructive" : ""}>
              <SelectValue placeholder="Select priority level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Critical">Critical</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
            </SelectContent>
          </Select>
          {errors.priority && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              Priority level is required
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">
          Detailed Description <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="description"
          placeholder="Provide a comprehensive description of what you're requesting..."
          rows={4}
          {...register("description", {
            required: "Description is required",
            minLength: {
              value: 20,
              message: "Description must be at least 20 characters"
            }
          })}
          className={errors.description ? "border-destructive" : ""}
        />
        {errors.description && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.description.message}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Department */}
        <div className="space-y-2">
          <Label htmlFor="department">
            Requesting Department <span className="text-destructive">*</span>
          </Label>
          <Select
            onValueChange={(value) => setValue("department", value)}
            value={watch("department")}
          >
            <SelectTrigger className={errors.department ? "border-destructive" : ""}>
              <SelectValue placeholder="Select your department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="IT">Information Technology</SelectItem>
              <SelectItem value="HR">Human Resources</SelectItem>
              <SelectItem value="Finance">Finance</SelectItem>
              <SelectItem value="Marketing">Marketing</SelectItem>
              <SelectItem value="Operations">Operations</SelectItem>
              <SelectItem value="Sales">Sales</SelectItem>
              <SelectItem value="Legal">Legal</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {errors.department && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              Department is required
            </div>
          )}
        </div>

        {/* Requested By */}
        <div className="space-y-2">
          <Label htmlFor="requestedBy">
            Requested By <span className="text-destructive">*</span>
          </Label>
          <Input
            id="requestedBy"
            placeholder="Your full name"
            {...register("requestedBy", {
              required: "Requester name is required"
            })}
            className={errors.requestedBy ? "border-destructive" : ""}
          />
          {errors.requestedBy && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {errors.requestedBy.message}
            </div>
          )}
        </div>
      </div>

      {/* Business Case */}
      <div className="space-y-2">
        <Label htmlFor="businessCase">
          Business Case <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="businessCase"
          placeholder="Explain the business need and how this demand will benefit the organization..."
          rows={4}
          {...register("businessCase", {
            required: "Business case is required",
            minLength: {
              value: 30,
              message: "Business case must be at least 30 characters"
            }
          })}
          className={errors.businessCase ? "border-destructive" : ""}
        />
        {errors.businessCase && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.businessCase.message}
          </div>
        )}
      </div>
    </div>
  );
}